import { useId, useState } from 'react';
import { Badge, Icon } from '@pryme/ui';
import { matchSecret } from '../lib/signing';
import type { ServiceKey } from '../lib/types';

/**
 * Paste field for the service's secret signing key. The value lives in React state only (memory):
 * it is never sent to Pryme, written to storage, or logged.
 */
export function SecretKeyInput(props: { value: string; onChange(v: string): void; keys?: ServiceKey[]; label?: string; autoFocus?: boolean }) {
  const id = useId();
  const [show, setShow] = useState(false);
  const match = props.keys && props.value.trim() ? matchSecret(props.value, props.keys) : null;
  return (
    <div className="p-field">
      <label htmlFor={id}>{props.label ?? 'Secret key (PRYME_SECRET_KEY)'}</label>
      <div className="p-row" style={{ flexWrap: 'nowrap' }}>
        <input
          id={id}
          className="p-input mono"
          type={show ? 'text' : 'password'}
          autoComplete="off"
          spellCheck={false}
          autoFocus={props.autoFocus}
          value={props.value}
          onChange={(e) => props.onChange(e.target.value)}
          placeholder="sk_test_… or sk_live_…"
          aria-describedby={`${id}-hint`}
        />
        <button type="button" className="p-btn secondary small" onClick={() => setShow((s) => !s)} aria-pressed={show}>
          {show ? 'Hide' : 'Show'}
        </button>
        {props.value && (
          <button type="button" className="p-btn ghost small" onClick={() => props.onChange('')}>
            Forget
          </button>
        )}
      </div>
      <span className="p-hint" id={`${id}-hint`} style={{ display: 'flex', gap: '0.35rem', alignItems: 'flex-start' }}>
        <Icon name="lock" size={13} />
        <span>Held in this tab’s memory only. Used to sign locally; never sent to Pryme and never stored. Reloading the page forgets it.</span>
      </span>
      {match && (
        <span role="status">
          {match.ok ? (
            <Badge tone="ok" icon="check">Matches active key {match.key.id} · {match.key.fingerprint}</Badge>
          ) : (
            <Badge tone="bad">{match.reason}</Badge>
          )}
        </span>
      )}
    </div>
  );
}
